import { Box, Text } from '@chakra-ui/react';
import { ChakraFormBaseInput } from 'app/components/Core';
import React, { useState } from 'react';
import { PlaylistValidations } from 'types/playlists.types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { getDirtyValues } from 'app/utils/utils';
import {
  getMaxTitleLength,
  getTitleValidations,
} from 'app/components/Playlist/PlaylistTitleForm/utils';

interface PlaylistTitleFormProps {
  title: string;
  validations?: PlaylistValidations;
  onUpdate?: (title: string) => Promise<void>;
}

interface Form {
  title: string;
}

export const PlaylistTitleForm = ({
  title,
  validations,
  onUpdate,
}: PlaylistTitleFormProps) => {
  const maxTitleLength = getMaxTitleLength(validations);
  const [lastValidTitle, setLastValidTitle] = useState(title);

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    watch,
    formState: { dirtyFields, errors, isValid },
  } = useForm<Form>({
    defaultValues: { title },
    mode: 'all',
    resolver: yupResolver(getTitleValidations(maxTitleLength)),
  });

  const onSubmit = handleSubmit(async (data: Form) => {
    reset(data);
    const dirtyValues = getDirtyValues<Form>(dirtyFields, data);
    if (dirtyValues.title) {
      try {
        await onUpdate?.(data.title);
      } catch (e) {
        // proceed
      }
    }
  });

  const watchTitle = watch('title');

  return (
    <Box position="relative" w="calc(50% - 144px)">
      <ChakraFormBaseInput
        placeholder="Title"
        {...register('title')}
        isInvalid={Boolean(errors?.title)}
        data-testid="playlist-title"
        onChange={(e) => {
          let value = e.target.value.trimStart();
          if (maxTitleLength && value.length > maxTitleLength) {
            value = value.substring(0, maxTitleLength);
          }
          setValue('title', value, {
            shouldDirty: true,
            shouldValidate: true,
          });
          if (value) {
            setLastValidTitle(value);
          }
        }}
        onBlur={(e) => {
          if (!e.target.value) {
            setValue('title', lastValidTitle, {
              shouldValidate: true,
              shouldDirty: false,
              shouldTouch: true,
            });
          }
          if (isValid) {
            onSubmit();
          }
        }}
        isDisabled={!onUpdate}
        fontSize="2xl"
        fontWeight={500}
        lineHeight="normal"
        _disabled={{
          cursor: 'default',
          opacity: 1,
        }}
      />
      {maxTitleLength && maxTitleLength - watchTitle.length <= 30 && (
        <Text position="absolute" right={0} color="textSubdued">
          {watchTitle.length}/{maxTitleLength}
        </Text>
      )}
    </Box>
  );
};
